<script setup lang="ts">
import CompetenceDisplayRow from '@/core/competences/CompetenceDisplayRow.vue';
import type { ICompetenceDisplayItem } from '@/core/competences/competenceDisplay.types';
import { uniqueCompetenceDisplayItems } from '@/core/competences/competenceDisplay.utils';
import CustomSeparator from '@/core/elements/CustomSeparator.vue';
import { computed, ref } from 'vue';

const props = withDefaults(
  defineProps<{
    expandable?: boolean;
    initialVisibleCount?: number;
    items: readonly ICompetenceDisplayItem[];
    separatorInset?: 'sm' | 'md';
    surface?: 'primary' | 'secondary';
  }>(),
  {
    expandable: true,
    initialVisibleCount: 2,
    separatorInset: 'sm',
    surface: 'primary',
  },
);

defineSlots<{
  footer?(): void;
  trailing?(props: { item: ICompetenceDisplayItem }): void;
}>();

const expanded = ref(false);
const uniqueItems = computed(() => uniqueCompetenceDisplayItems(props.items));
const hasOverflow = computed(() => props.expandable && uniqueItems.value.length > props.initialVisibleCount);
const visibleItems = computed(() =>
  hasOverflow.value && !expanded.value ? uniqueItems.value.slice(0, props.initialVisibleCount) : uniqueItems.value,
);
const hiddenItemCount = computed(() => uniqueItems.value.length - props.initialVisibleCount);
const separatorClass = computed(() => (props.separatorInset === 'md' ? 'mx-md my-sm' : 'mx-sm my-sm'));
</script>

<template>
  <div
    v-if="uniqueItems.length || $slots.footer"
    class="w-full rounded-2xl py-sm"
    :class="props.surface === 'secondary' ? 'bg-surface-secondary' : 'bg-surface-primary shadow-cookie-banner'"
  >
    <template v-for="(item, index) in visibleItems" :key="item.id">
      <CustomSeparator v-if="index > 0" :class="separatorClass" />
      <CompetenceDisplayRow :item="item">
        <template v-if="$slots.trailing" #trailing>
          <slot name="trailing" :item="item" />
        </template>
      </CompetenceDisplayRow>
    </template>

    <template v-if="hasOverflow">
      <button
        type="button"
        class="px-lg py-sm text-left text-base text-secondary underline outline-inner transition-colors hover:text-primary active:text-primary [text-decoration-skip-ink:none] [text-underline-position:from-font]"
        @click.stop="expanded = !expanded"
        @keydown.stop
      >
        {{ expanded ? $t('timeline.showLessCompetences') : $t('timeline.moreCompetences', { count: hiddenItemCount }) }}
      </button>
    </template>

    <template v-if="$slots.footer">
      <CustomSeparator v-if="uniqueItems.length" :class="separatorClass" />
      <slot name="footer" />
    </template>
  </div>
</template>
